import React, { useEffect } from "react";
import { Table, Button, Input, Card, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Big from "big.js";
import {
  setFinishedItem,
  setNarration,
  addBomItemRow,
  updateBomItemRow,
  deleteBomItemRow,
  resetBomForm,
} from "../bomSlice";
import { saveBom, fetchBomList } from "../bomThunk";
import { fetchItems } from "../../../../store/commonThunks/lookups";
import { getCurrentStockApi } from "../../../../apiservice/sales";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import InputNumberField from "../../../../components/Input/InputNumber";

const { Title } = Typography;

const bigMul = (a, b) => Number(Big(a || 0).times(b || 0).toFixed(13));

const BOMForm = ({ onDone }) => {
  const dispatch = useDispatch();
  const { items } = useSelector((s) => s.lookups);
  const { bomId, finishedItemId, narration, itemsCart, isEdit } = useSelector(
    (s) => s.bom
  );

  useEffect(() => {
    dispatch(fetchItems());
  }, [dispatch]);

  const finishedItemOptions = (items || [])
    .filter((i) => i.category_type === "finished_product")
    .map((i) => ({ sale_id: i.item_id, display_text: i.item_name, raw: i }));

  const rawMaterialOptions = (items || [])
    .filter((i) => i.category_type === "raw_material")
    .map((i) => ({ sale_id: i.item_id, display_text: i.item_name, raw: i }));

  const finishedItem = items.find((i) => i.item_id === finishedItemId);

  const handleRawItemSelect = (rowKey, selectedItem) => {
    if (!selectedItem) return;
    dispatch(
      updateBomItemRow({
        rowKey,
        updates: {
          item_id: selectedItem.item_id,
          product: selectedItem.item_name,
          unit_symbol: selectedItem.unit_symbol || "",
          per_unit_id: selectedItem.unit_id || 0,
        },
      })
    );

    getCurrentStockApi({ itemId: selectedItem.item_id, warehouseId: 0 })
      .then((res) => {
        const avgRate = parseFloat(res.data[0]?.average_rate || 0);
        dispatch(updateBomItemRow({ rowKey, updates: { average_rate: avgRate } }));
      })
      .catch(() => {});
  };

  const totalMaterialCost = itemsCart.reduce(
    (sum, r) => sum + bigMul(r.quantity, r.average_rate || 0),
    0
  );

  const columns = [
    {
      title: "Raw Material",
      render: (_, record) => (
        <SearchListAutoComplete
          entityType="item"
          placeholder="Select Raw Material"
          style={{ width: "100%" }}
          optionsList={rawMaterialOptions}
          value={
            record.item_id
              ? { value: record.product, label: record.product }
              : undefined
          }
          onSelect={(obj) => {
            if (!obj || obj === "add_new") return;
            handleRawItemSelect(record.key, obj.raw);
          }}
        />
      ),
    },
    {
      title: "Quantity (per 1 unit of output)",
      render: (_, record) => (
        <InputNumberField
          min={0}
          precision={4}
          value={record.quantity}
          onChange={(v) =>
            dispatch(updateBomItemRow({ rowKey: record.key, updates: { quantity: v || 0 } }))
          }
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Current Cost / Unit",
      render: (_, record) => Number(record.average_rate || 0).toFixed(2),
    },
    {
      title: "Line Cost",
      render: (_, record) => bigMul(record.quantity, record.average_rate || 0).toFixed(2),
    },
    {
      title: "",
      render: (_, record) => (
        <Button
          icon={<DeleteOutlined />}
          danger
          onClick={() => dispatch(deleteBomItemRow(record.key))}
          disabled={itemsCart.length === 1}
        />
      ),
    },
  ];

  const handleSave = () => {
    if (!finishedItemId) {
      return Swal.fire("Please select a finished product", "", "warning");
    }
    const validItems = itemsCart.filter((r) => r.item_id && Number(r.quantity) > 0);
    if (validItems.length === 0) {
      return Swal.fire("Add at least one raw material with a quantity", "", "warning");
    }

    dispatch(
      saveBom({
        action: isEdit ? "update" : "create",
        bom_id: bomId,
        item_id: finishedItemId,
        narration,
        items: validItems.map((r) => ({
          item_id: r.item_id,
          quantity: r.quantity,
          per_unit_id: r.per_unit_id,
        })),
      })
    )
      .unwrap()
      .then(() => {
        Swal.fire("Recipe saved", "", "success");
        dispatch(fetchBomList());
        dispatch(resetBomForm());
        onDone();
      })
      .catch((err) => {
        Swal.fire(err?.message || "Save failed", "", "warning");
      });
  };

  return (
    <div>
      <Title level={3}>{isEdit ? "Edit Recipe" : "Create Recipe"}</Title>

      <Card style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 12 }}>
          <label>Finished Product</label>
          <SearchListAutoComplete
            entityType="item"
            placeholder="Select Finished Product"
            style={{ width: "100%" }}
            optionsList={finishedItemOptions}
            disabled={isEdit}
            value={
              finishedItemId
                ? { value: finishedItem?.item_name, label: finishedItem?.item_name }
                : undefined
            }
            onSelect={(obj) => {
              if (!obj || obj === "add_new") return;
              dispatch(setFinishedItem({ item_id: obj.raw.item_id, product: obj.raw.item_name }));
            }}
          />
        </div>
        <div>
          <label>Narration</label>
          <Input.TextArea
            value={narration}
            onChange={(e) => dispatch(setNarration(e.target.value))}
            rows={2}
          />
        </div>
      </Card>

      <Card title="Raw Materials" style={{ marginBottom: 20 }}>
        <Table dataSource={itemsCart} columns={columns} rowKey="key" pagination={false} />
        <Button
          type="dashed"
          onClick={() => dispatch(addBomItemRow())}
          icon={<PlusOutlined />}
          style={{ marginTop: 10 }}
        >
          Add Row
        </Button>

        <div style={{ marginTop: 16, fontWeight: "bold" }}>
          Total Material Cost: {totalMaterialCost.toFixed(2)}
        </div>
      </Card>

      <Button
        onClick={() => {
          dispatch(resetBomForm());
          onDone();
        }}
        style={{ marginRight: 12 }}
      >
        Cancel
      </Button>
      <Button type="primary" onClick={handleSave}>
        Save Recipe
      </Button>
    </div>
  );
};

export default BOMForm;
